import React, { type CSSProperties } from 'react';
import { Button, FilterPopover } from '@/components/ui';
import type { FilterPopoverTriggerState } from '@/components/ui';
import { ChevronDownIcon, FunnelIcon } from './icons';
import { AuditDatePresetList } from './AuditDatePresetList';
import {
    AUDIT_DATE_PRESETS,
    AUDIT_DATE_PRESET_ORDER,
    type AuditDatePresetLabels,
    type AuditDateRangeValue,
} from './auditDatePresets';

export interface AuditDateRangeChipProps {
    label: string;
    value: AuditDateRangeValue;
    onChange: (next: AuditDateRangeValue) => void;
    /**
     * Copy overrides. Defaulted in `AuditDatePresetList` — and only there —
     * because the presets are this screen's own vocabulary rather than the
     * caller's domain: a caller cannot add a seventh preset, so requiring it to
     * restate all six as props would be ceremony. Everything else on this bar
     * still arrives as a prop.
     */
    labels?: AuditDatePresetLabels;
    className?: string;
    style?: CSSProperties;
}

/**
 * EAA-1821: the "Thời gian" chip — the same shell as `AuditFilterChip` but
 * single-select, because a time window is one range and stacking two of them
 * has no meaning. `Tất cả` is modelled as a preset rather than as "no preset"
 * so the popover always has a row that clears the facet, matching the implicit
 * "Tất cả" option every other console filter carries.
 *
 * The menu body is `AuditDatePresetList`, which the TABLE HEADER's "Thời điểm"
 * funnel renders too. This file used to carry its own copy of that markup: the
 * two then had to be kept in step by hand, which is precisely how the header
 * filter and the bar filter drift into disagreeing about the one date range
 * they both write. Only the trigger — the readout — is this component's own.
 */
function AuditDateRangeChipBase({
    label,
    value,
    onChange,
    labels,
    className,
    style,
}: AuditDateRangeChipProps): React.ReactElement {
    const active = value.preset !== 'all';
    const presetLabel = labels?.presets?.[value.preset] ?? AUDIT_DATE_PRESETS[value.preset];

    const chipClassName = ['portal-audit-filter-chip', active ? 'is-active' : '', className ?? '']
        .filter(Boolean)
        .join(' ');

    const renderTrigger = ({ open, ref, toggle }: FilterPopoverTriggerState) => (
        <Button
            ref={ref}
            className={chipClassName}
            style={style}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={toggle}
        >
            <FunnelIcon className="portal-audit-filter-chip__icon" />
            <span className="portal-audit-filter-chip__label">{label}:</span>
            <span className="portal-audit-filter-chip__value">{presetLabel}</span>
            {/* EAA-1821: set, the badge counts the ONE window in force; unset, it
                advertises how many presets are on offer — the same readout rule
                the multi-select chips follow, so the bar scans consistently. */}
            <span className="portal-audit-filter-chip__badge">
                {active ? 1 : AUDIT_DATE_PRESET_ORDER.length}
            </span>
            <ChevronDownIcon className="portal-audit-filter-chip__icon" />
        </Button>
    );

    return (
        <FilterPopover className="portal-audit-filter-chip-root" trigger={renderTrigger}>
            <AuditDatePresetList label={label} value={value} onChange={onChange} labels={labels} />
        </FilterPopover>
    );
}

/** EAA-1821: memoized for the same reason as `AuditFilterChip` — see its note. */
export const AuditDateRangeChip = React.memo(AuditDateRangeChipBase);
